// The Monitoring RUN/TOOL-CALL TRACE list (N-FDE-2 AC-1) — one row per Agent Run: the initiating actor class,
// what triggered it, its lifecycle state, the per-run token usage + £ cost, and a "view transcript" action.
//
// SCOPE HONESTLY (N-FDE-2 AC-6, G-180): the verbatim transcript is ACTOR-SCOPED at the OWNING agent read. The
// row's "view transcript" routes the actor-scoped read; the surface renders EXACTLY the verdict it gets — an
// `allowed` verdict renders the ordered conversational TURNS ({id,author,text}), a `refused` verdict shows an
// honest "not in your scope" line and NO transcript. The dashboard widens nothing: there are no turns on a
// refused verdict, so there is nothing to leak (and no substrate pointer flows on either branch, AR-HARNESS-2).
// The boundary is the owning interface; this component only carries the verdict through (P-SELF). Tokens only
// (G-181); a run's state marker DERIVES from the run's lifecycle (G-182).

import { useCallback, useState } from 'react';
import { Panel, EmptyState, StatusPill } from '../primitives.tsx';
import { MetaLine, formatPence, runStateTone } from './operate-primitives.tsx';
import type { AgentRunView, RunTranscriptProjection } from '../operate-projections.ts';

export interface RunTraceListProps {
  readonly runs: readonly AgentRunView[];
  /** Route the ACTOR-SCOPED transcript read for one run (the OWNING agent read enforces the scope). */
  readonly onViewTranscript: (runId: string) => Promise<RunTranscriptProjection>;
}

/** The run-trace list — a card per run with its cost/token figures and a scope-enforced transcript action. */
export function RunTraceList({ runs, onViewTranscript }: RunTraceListProps): React.JSX.Element {
  if (runs.length === 0) {
    return (
      <Panel>
        <EmptyState>No agent runs in this window.</EmptyState>
      </Panel>
    );
  }
  return (
    <ul className="space-y-3" aria-label="Agent run traces">
      {runs.map((run) => (
        <li key={run.id}>
          <RunTraceCard run={run} onViewTranscript={onViewTranscript} />
        </li>
      ))}
    </ul>
  );
}

/** One run trace card — the run's class/state/cost + the scope-enforced transcript reveal. */
function RunTraceCard({
  run,
  onViewTranscript,
}: {
  readonly run: AgentRunView;
  readonly onViewTranscript: (runId: string) => Promise<RunTranscriptProjection>;
}): React.JSX.Element {
  const initiator = run.initiatingActor.kind === 'human' ? 'a person' : 'the system';
  return (
    <Panel className="px-5 py-4" dataMarker={{ name: 'run-trace', value: run.id }}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusPill tone={runStateTone(run.state)}>{run.state}</StatusPill>
            <span className="text-sm text-text-muted">initiated by {initiator}</span>
          </div>
          <div className="mt-2 space-y-0.5">
            <MetaLine label="Trigger">{run.trigger}</MetaLine>
            <MetaLine label="Tokens">{run.tokenUsage.totalTokens.toLocaleString('en-GB')}</MetaLine>
            {run.failureReason !== undefined ? (
              <MetaLine label="Reason">{run.failureReason}</MetaLine>
            ) : null}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="ritona-numeric text-2xl text-text" data-run-cost={run.id}>
            {formatPence(run.costPence)}
          </div>
          <div className="ritona-numeric text-xs text-text-subtle">{run.startedAt}</div>
        </div>
      </div>
      <TranscriptReveal runId={run.id} onViewTranscript={onViewTranscript} />
    </Panel>
  );
}

/**
 * The scope-enforced transcript reveal — routes the ACTOR-SCOPED read and renders EXACTLY the OWNING verdict:
 * an `allowed` verdict renders the ordered conversational TURNS, a `refused` verdict renders NO transcript (the
 * surface widens nothing, N-FDE-2 AC-6 / G-180). No substrate pointer flows on either branch (AR-HARNESS-2).
 * The boundary is the owning interface; this only carries the verdict through and renders what it carries.
 */
function TranscriptReveal({
  runId,
  onViewTranscript,
}: {
  readonly runId: string;
  readonly onViewTranscript: (runId: string) => Promise<RunTranscriptProjection>;
}): React.JSX.Element {
  const [verdict, setVerdict] = useState<RunTranscriptProjection | null>(null);
  const onReveal = useCallback(async () => {
    setVerdict(await onViewTranscript(runId));
  }, [onViewTranscript, runId]);

  return (
    <div className="mt-3 border-t border-border pt-3">
      {verdict === null ? (
        <button
          type="button"
          data-view-transcript={runId}
          onClick={() => void onReveal()}
          className="rounded-sm border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text hover:border-border-strong"
        >
          View transcript
        </button>
      ) : verdict.verdict.allowed ? (
        <ol className="space-y-2" data-transcript-turns={runId} aria-label="Run transcript">
          {verdict.verdict.turns.map((turn) => (
            <li key={turn.id} data-transcript-turn={turn.id} className="text-sm">
              <span className="text-text-subtle">
                {turn.author === 'human'
                  ? 'Person'
                  : turn.author === 'platform'
                    ? 'Platform'
                    : 'Agent'}
                :{' '}
              </span>
              <span className="text-text">{turn.text}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-text-subtle" data-transcript-refused={runId}>
          This run's transcript is outside your monitoring scope. Nothing is shown.
        </p>
      )}
    </div>
  );
}
